import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, TrendingUp, ChevronLeft, Building2, Calendar } from 'lucide-react';
import { Card } from '../ui/Card';

/**
 * UserZonesSummary Component
 *
 * Shows summary cards for each of the user's zones with member counts and stats
 */
const UserZonesSummary = ({ userZones, chatId, safeFetch, navigate }) => {
  const [zonesData, setZonesData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchZonesData = async () => {
      if (!userZones || userZones.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const promises = userZones.map(async (zoneId) => {
          try {
            // Fetch zone members
            const response = await safeFetch(
              `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zone-members?zone_id=${zoneId}&chat_id=${chatId}`,
              chatId
            );

            if (response.ok) {
              const data = await response.json();
              const members = data.members || [];
              return {
                zone_id: zoneId,
                member_count: data.member_count || members.length,
                members: members,
                is_member: data.is_current_user_member || false
              };
            }
            return { zone_id: zoneId, member_count: 0, members: [], is_member: false };
          } catch (err) {
            console.error(`Error fetching data for zone ${zoneId}:`, err);
            return { zone_id: zoneId, member_count: 0, members: [], is_member: false };
          }
        });

        const results = await Promise.all(promises);
        const dataMap = {};
        results.forEach(result => {
          dataMap[result.zone_id] = result;
        });
        setZonesData(dataMap);
      } catch (err) {
        console.error('Error fetching zones data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchZonesData();
  }, [userZones, chatId]);

  if (loading) {
    return (
      <div className="space-y-4" style={{ direction: 'rtl' }}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-200 border-t-brand-500"></div>
        </div>
      </div>
    );
  }

  if (!userZones || userZones.length === 0) {
    return null;
  }

  // Calculate total members across all zones
  const totalMembers = Object.values(zonesData).reduce((sum, zone) => sum + (zone.member_count || 0), 0);

  return (
    <div className="space-y-3" style={{ direction: 'rtl' }}>
      {/* Zone Cards - Compact & Beautiful */}
      <div className="space-y-2.5">
        {userZones.map((zoneId, index) => {
          const zoneData = zonesData[zoneId] || { member_count: 0, members: [], is_member: false };

          // Rotate through vibrant color schemes
          const colorSchemes = [
            {
              bg: 'from-info-50/60 via-brand-50/40 to-white',
              border: 'border-info-100/50 hover:border-info-500/60',
              accent: 'from-info-500 via-brand-500 to-brand-600',
              icon: 'from-info-500 via-brand-600 to-brand-600',
              iconHover: 'group-hover:shadow-info-100/50',
              overlay: 'from-info-100/40',
              badge: 'from-info-50/80 to-brand-50/70 border-info-100/40',
              badgeText: 'text-info-700',
              badgeIcon: 'text-brand-600',
              avatarBg: 'from-info-100/40 to-brand-200/40',
              avatarText: 'text-info-700',
              counter: 'text-info-700 bg-info-50 border-info-100/50',
              arrow: 'text-info-500/60 group-hover:text-info-600',
              titleHover: 'group-hover:text-brand-600'
            },
            {
              bg: 'from-success-50/60 via-success-50/40 to-white',
              border: 'border-success-100/50 hover:border-success-500/60',
              accent: 'from-success-500 via-success-500 to-info-600',
              icon: 'from-success-500 via-success-600 to-info-600',
              iconHover: 'group-hover:shadow-success-100/50',
              overlay: 'from-success-100/40',
              badge: 'from-success-50/80 to-success-50/70 border-success-100/40',
              badgeText: 'text-success-700',
              badgeIcon: 'text-success-600',
              avatarBg: 'from-success-100/40 to-success-100/40',
              avatarText: 'text-success-700',
              counter: 'text-success-700 bg-success-50 border-success-100/50',
              arrow: 'text-success-500/60 group-hover:text-success-600',
              titleHover: 'group-hover:text-success-600'
            },
            {
              bg: 'from-secondary-50/60 via-secondary-50/40 to-white',
              border: 'border-secondary-200/50 hover:border-secondary-400/60',
              accent: 'from-secondary-400 via-secondary-500 to-brand-600',
              icon: 'from-secondary-500 via-secondary-600 to-brand-600',
              iconHover: 'group-hover:shadow-secondary-300/50',
              overlay: 'from-secondary-100/40',
              badge: 'from-secondary-50/80 to-secondary-50/70 border-secondary-200/40',
              badgeText: 'text-secondary-700',
              badgeIcon: 'text-secondary-600',
              avatarBg: 'from-secondary-200/40 to-secondary-200/40',
              avatarText: 'text-secondary-700',
              counter: 'text-secondary-700 bg-secondary-50 border-secondary-200/50',
              arrow: 'text-secondary-400/60 group-hover:text-secondary-600',
              titleHover: 'group-hover:text-secondary-600'
            },
            {
              bg: 'from-warning-50/60 via-warning-50/40 to-white',
              border: 'border-warning-100/50 hover:border-warning-500/60',
              accent: 'from-warning-500 via-warning-500 to-danger-600',
              icon: 'from-warning-500 via-warning-600 to-danger-600',
              iconHover: 'group-hover:shadow-warning-100/50',
              overlay: 'from-warning-100/40',
              badge: 'from-warning-50/80 to-warning-50/70 border-warning-100/40',
              badgeText: 'text-warning-700',
              badgeIcon: 'text-warning-600',
              avatarBg: 'from-warning-100/40 to-warning-100/40',
              avatarText: 'text-warning-700',
              counter: 'text-warning-700 bg-warning-50 border-warning-100/50',
              arrow: 'text-warning-500/60 group-hover:text-warning-600',
              titleHover: 'group-hover:text-warning-600'
            },
            {
              bg: 'from-brand-50/60 via-brand-50/40 to-white',
              border: 'border-brand-200/50 hover:border-brand-400/60',
              accent: 'from-brand-400 via-brand-500 to-brand-600',
              icon: 'from-brand-500 via-brand-600 to-brand-600',
              iconHover: 'group-hover:shadow-brand-300/50',
              overlay: 'from-brand-100/40',
              badge: 'from-brand-50/80 to-brand-50/70 border-brand-200/40',
              badgeText: 'text-brand-700',
              badgeIcon: 'text-brand-600',
              avatarBg: 'from-brand-200/40 to-brand-200/40',
              avatarText: 'text-brand-700',
              counter: 'text-brand-700 bg-brand-50 border-brand-200/50',
              arrow: 'text-brand-400/60 group-hover:text-brand-600',
              titleHover: 'group-hover:text-brand-600'
            }
          ];

          const colors = colorSchemes[index % colorSchemes.length];

          return (
            <motion.div
              key={zoneId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ scale: 1.01, transition: { duration: 0.15 } }}
            >
              <div
                onClick={() => navigate('zone-details', `zone=${encodeURIComponent(zoneId)}`)}
                className={`group relative bg-gradient-to-l ${colors.bg} rounded-xl p-4 cursor-pointer transition-all duration-200 border ${colors.border} shadow-sm hover:shadow-lg overflow-hidden`}
              >
                {/* Animated gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-l ${colors.overlay} via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                {/* Accent bar with vibrant gradient */}
                <div className={`absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-b ${colors.accent} opacity-70 group-hover:opacity-100 transition-opacity duration-300 shadow-md`} />

                {/* Subtle top gradient accent */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-l ${colors.accent} opacity-20`} />

                <div className="relative z-10 flex items-center gap-3">
                  {/* Icon */}
                  <div className="relative flex-shrink-0">
                    <div className={`w-12 h-12 bg-gradient-to-br ${colors.icon} rounded-xl flex items-center justify-center shadow-md ${colors.iconHover} group-hover:shadow-lg transition-all duration-200 group-hover:scale-105`}>
                      <MapPin className="w-5 h-5 text-white drop-shadow-md group-hover:scale-110 transition-transform duration-200" strokeWidth={2.5} />
                    </div>
                    {zoneData.is_member && (
                      <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-gradient-to-br from-success-500 to-success-600 rounded-full border-2 border-white shadow-md animate-pulse" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-bold text-ink-strong text-base truncate ${colors.titleHover} transition-colors duration-200`}>{zoneId}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        {/* Member count */}
                        <div className={`flex items-center gap-1 px-2 py-0.5 bg-gradient-to-br ${colors.badge} rounded-md text-xs ${colors.badgeText} border`}>
                          <Users className={`w-3.5 h-3.5 ${colors.badgeIcon}`} strokeWidth={2.5} />
                          <span className="font-bold">{zoneData.member_count}</span>
                          <span className="font-medium">חברים</span>
                        </div>

                        {/* Member avatars */}
                        {zoneData.members && zoneData.members.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            <div className="flex -space-x-1.5">
                              {zoneData.members.slice(0, 3).map((member, idx) => (
                                <div
                                  key={idx}
                                  className={`w-6 h-6 rounded-full bg-gradient-to-br ${colors.avatarBg} border-2 border-white flex items-center justify-center shadow-md hover:scale-125 hover:z-10 transition-transform duration-200`}
                                  title={member.name || member.user_id}
                                >
                                  {member.profile_image ? (
                                    <img
                                      src={member.profile_image}
                                      alt={member.name}
                                      className="w-full h-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <span className={`text-[10px] font-bold ${colors.avatarText}`}>
                                      {(member.name || member.user_id).charAt(0).toUpperCase()}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                            {zoneData.member_count > 3 && (
                              <span className={`text-[10px] font-bold ${colors.counter} px-1.5 py-0.5 rounded border`}>
                                +{zoneData.member_count - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronLeft className={`w-4 h-4 ${colors.arrow} flex-shrink-0 transform group-hover:translate-x-[-2px] transition-all duration-200`} strokeWidth={2.5} />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default UserZonesSummary;
